import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Navigation from "../components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ResetPasswordPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const query = useQuery();
  const tokenFromUrl = query.get("token") || "";

  const [mode, setMode] = useState(tokenFromUrl ? "reset" : "request");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(tokenFromUrl);
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("If an account exists for this email, a reset link has been sent.");
      setMode("reset");
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error("Unable to send reset email. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      toast.success("Password reset successfully. You can now log in.");
      navigate("/login");
    } catch (error) {
      console.error("Reset password error:", error);
      const detail = error.response?.data?.detail || "Unable to reset password.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const isResetMode = mode === "reset";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <div className="flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 shadow-sm">
          <h1
            className="text-2xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            {isResetMode ? "Reset your password" : "Forgot your password?"}
          </h1>
          <p className="text-sm text-slate-600 mb-6">
            {isResetMode
              ? "Enter the reset token you received by email (if not already filled) and choose a new password."
              : "Enter your email address and we'll send you a link to reset your password if an account exists."}
          </p>

          {!isResetMode ? (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email address</label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send reset link"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Reset token</label>
                <Input
                  type="text"
                  required
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste token from email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">New password</label>
                <Input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter a new password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset password"}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
